import { Controller } from "@hotwired/stimulus"

interface ModeCapabilities {
  output: string         // "image" | "video"
  inputImages: string    // "none" | "any" | "exactly_one"
  promptRequired: string // "true" | "false"
}

export default class CompetitionFormController extends Controller {
  static targets = ["maxGenerations", "videoDuration", "inputImages", "comparisonSlider", "typeSelector", "categoryGroup", "prompt", "scaleFactor"]
  static values = { type: { type: String, default: "text_to_image" } }

  declare maxGenerationsTarget: HTMLInputElement
  declare videoDurationTarget: HTMLElement
  declare inputImagesTarget: HTMLElement
  declare comparisonSliderTarget: HTMLElement
  declare typeSelectorTarget: HTMLElement
  declare categoryGroupTargets: HTMLElement[]
  declare promptTarget: HTMLElement
  declare scaleFactorTarget: HTMLElement
  declare typeValue: string
  declare hasMaxGenerationsTarget: boolean
  declare hasVideoDurationTarget: boolean
  declare hasInputImagesTarget: boolean
  declare hasComparisonSliderTarget: boolean
  declare hasPromptTarget: boolean
  declare hasScaleFactorTarget: boolean

  private userEditedMaxGen = false

  connect() {
    if (this.hasMaxGenerationsTarget) {
      this.maxGenerationsTarget.addEventListener("input", () => {
        this.userEditedMaxGen = true
      })
    }
    this.updateVisibility()
  }

  typeChanged(event: Event) {
    const target = event.target as HTMLInputElement
    this.typeValue = target.value
    this.updateVisibility()

    if (!this.userEditedMaxGen && this.hasMaxGenerationsTarget) {
      const caps = this.capabilitiesForType(this.typeValue)
      // One generation per model when output is video or there's a fixed input
      // image; multiple makes sense for divergent text-prompt outputs.
      const single = caps?.output === "video" || caps?.inputImages === "exactly_one"
      this.maxGenerationsTarget.value = single ? "1" : "3"
    }
  }

  private capabilitiesForType(type: string): ModeCapabilities | null {
    const radio = this.typeSelectorTarget.querySelector<HTMLInputElement>(
      `input[type=radio][value="${type}"]`
    )
    if (!radio) return null
    return {
      output: radio.dataset.output ?? "image",
      inputImages: radio.dataset.inputImages ?? "none",
      promptRequired: radio.dataset.promptRequired ?? "true"
    }
  }

  private updateVisibility() {
    const type = this.typeValue
    const caps = this.capabilitiesForType(type)
    const isVideo   = caps?.output === "video"
    const isUpscale = type === "image_upscaling"
    const isEditing = type === "image_editing"
    const needsInput = (caps?.inputImages ?? "none") !== "none"
    const promptOptional = caps?.promptRequired === "false"

    if (this.hasVideoDurationTarget)    this.videoDurationTarget.hidden    = !isVideo
    if (this.hasScaleFactorTarget)      this.scaleFactorTarget.hidden      = !isUpscale
    if (this.hasInputImagesTarget)      this.inputImagesTarget.hidden      = !needsInput
    if (this.hasComparisonSliderTarget) this.comparisonSliderTarget.hidden = !isEditing
    // Prompt field visible whenever the mode allows a prompt; only image_upscaling
    // truly omits it since it has no semantic prompt concept.
    if (this.hasPromptTarget)           this.promptTarget.hidden           = isUpscale

    this.categoryGroupTargets.forEach(group => {
      const isActive = group.dataset.categoryType === type
      group.hidden = !isActive
      group.querySelectorAll<HTMLInputElement>("input[type=checkbox]").forEach(cb => {
        cb.disabled = !isActive
      })
    })
  }
}
